import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const FILE_BASE = BACKEND_URL;

const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    // Give the pod up to 30 s to answer — cold-starts can be slow but almost
    // never longer than this. Prevents "no response" errors during warm-up.
    timeout: 30_000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("micm_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // v1.25.1 — Super User "Switch Company" impersonation. When set, every
    // request is scoped to the impersonated company_id so the SU sees exactly
    // what a Company Admin of that tenant would see.
    const impersonate = localStorage.getItem("micm_impersonate_company");
    if (impersonate) {
        config.headers["X-Impersonate-Company"] = impersonate;
    }
    return config;
});

// ── Retry & session-recovery interceptor ───────────────────────────────────
// GET requests retry up to 3 times on transient errors (cold-start 404/502/…,
// network errors, timeouts). Non-GET verbs retry on true network errors AND on
// ingress-level 404/502/503/504 responses (cold-start signatures) up to 2 times
// — safe because these codes mean the request never reached application code.
// On 401, we clear the token and bounce the user to /login (once, globally).
const RETRY_STATUSES = new Set([404, 502, 503, 504]);
let redirectingToLogin = false;

const isSafeToRetryMutation = (error) =>
    !error?.response &&                                          // no response = network / timeout
    (error?.code === "ECONNABORTED" ||                           // axios timeout
     error?.code === "ERR_NETWORK" ||
     error?.message?.toLowerCase().includes("network"));

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const cfg = error?.config;
        const status = error?.response?.status;
        const method = (cfg?.method || "get").toLowerCase();

        // ── 401: session expired / invalid token — kick to /login gracefully ──
        // Skip on /auth/me (used to *check* auth on mount) and /auth/login itself.
        const url = cfg?.url || "";
        const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login");
        // v1.29.41 — per-request opt-out: payment/verify actions pass
        // skipAuthRedirect so a 401 surfaces as an inline form error instead
        // of hard-redirecting the whole app to /login mid-action.
        if (status === 401 && !isAuthProbe && !cfg?.skipAuthRedirect && !redirectingToLogin) {
            redirectingToLogin = true;
            // v1.28.96 — Wipe the token so /login treats this as a fresh
            // session. Do NOT wipe `micm_impersonate_company` any more —
            // the previous behaviour dropped the SU's tenant context on
            // any transient 401 (aborted request during a hard reload,
            // stale impersonation header while switching, cold-start
            // race), which is what made "closing Upload Slips" land the
            // user on the empty super dashboard. Keeping the
            // impersonation value in localStorage means the moment the
            // SU logs back in they resume the same tenant they were on.
            try { localStorage.removeItem("micm_token"); } catch (_e) {}
            const path = window.location.pathname;
            if (path !== "/login") {
                // Preserve the intended destination so post-login redirect works
                const next = encodeURIComponent(path + window.location.search);
                setTimeout(() => {
                    window.location.assign(`/login?session=expired&next=${next}`);
                }, 150);
            }
            return Promise.reject(error);
        }

        if (!cfg) return Promise.reject(error);

        // ── GET retries on transient failures ──
        if (method === "get") {
            const retryable = RETRY_STATUSES.has(status) || !error.response || isSafeToRetryMutation(error);
            if (retryable) {
                cfg.__retryCount = (cfg.__retryCount || 0) + 1;
                if (cfg.__retryCount <= 3) {
                    const delay = 500 + 700 * cfg.__retryCount;
                    await new Promise((res) => setTimeout(res, delay));
                    return api.request(cfg);
                }
            }
            return Promise.reject(error);
        }

        // ── Non-GET: retry on true network errors AND on cold-start-style
        // ingress errors (404/502/503/504). These statuses on a mutation almost
        // always mean the request never reached FastAPI (Kubernetes route not
        // ready or Cloudflare bad-gateway during pod warm-up), so re-issuing
        // won't produce duplicates.
        const coldStartLike = isSafeToRetryMutation(error) || RETRY_STATUSES.has(status);
        if (coldStartLike) {
            cfg.__retryCount = (cfg.__retryCount || 0) + 1;
            if (cfg.__retryCount <= 2) {
                const delay = 700 + 900 * cfg.__retryCount;
                await new Promise((res) => setTimeout(res, delay));
                return api.request(cfg);
            }
        }
        return Promise.reject(error);
    }
);

export function formatApiError(err) {
    // v1.28.05 — Always surface a plain-English message. Never expose
    // "Request failed with status code 500" or a bare HTTP code to the user.
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    const HUMAN_BY_STATUS = {
        400: "The details you entered don't look right. Please check the form and try again.",
        401: "Your session has expired. Please sign in again.",
        403: "You don't have permission to perform this action.",
        404: "The requested record was not found.",
        409: "This record already exists. Please check for duplicates.",
        413: "The file you're uploading is too large.",
        422: "Some fields are missing or invalid. Please review the form.",
        429: "Too many attempts — please wait a moment and try again.",
        500: "Something went wrong on our end. Please try again or contact support.",
        502: "Our server is briefly unavailable. Please try again in a moment.",
        503: "Service temporarily unavailable. Please try again shortly.",
        504: "The request took too long. Please try again.",
    };

    // Prefer server-supplied detail (usually the most specific, e.g.
    // "Mobile number already registered to another user").
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const joined = detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : (e && typeof e === "object" ? (e.message || e.error || e.reason || "") : String(e))))
            .filter(Boolean)
            .join(" · ");
        if (joined) return joined;
    }
    if (detail && typeof detail === "object") {
        const guess = detail.message || detail.error || detail.reason || detail.title || detail.msg;
        if (typeof guess === "string" && guess) return guess;
    }

    // Cloudflare / upstream HTML page.
    const raw = err?.response?.data;
    if (typeof raw === "string" && raw.includes("<html")) {
        return HUMAN_BY_STATUS[status] || "The request took too long or the upstream service is down. Please try again.";
    }

    if (status && HUMAN_BY_STATUS[status]) return HUMAN_BY_STATUS[status];

    // Axios default message is "Request failed with status code XXX" — swap it.
    const msg = err?.message || "";
    if (/Request failed with status code/i.test(msg)) {
        return HUMAN_BY_STATUS[status] || "Something went wrong. Please try again.";
    }
    if (/Network Error/i.test(msg)) return "No internet connection or the server is unreachable.";
    if (msg) return msg;
    return "Something went wrong. Please try again.";
}

export default api;
